import { Button, KvixIcon, StatusText, Text } from "@kvix/ui";
import { Box, Divider, Link, useMediaQuery, useTheme } from "@material-ui/core";
import { format, formatDistanceToNow, isSameDay, isSameYear } from "date-fns";
import { sv } from "date-fns/locale";
import i18next from "i18next";
import React, {
  FC,
  Fragment,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useMount } from "react-use";
import StripeInternal from "stripe";
import styled from "styled-components";
import { useIsNativeApp } from "../../../../../../hooks/nativeApp";
import { formatCurrency } from "../../../../../../utils/formatCurrency";
import { PaymentContext } from "../../../../Payment/context";
import { ProfileSection } from "../../../partials/Section";

type RowSize = "small" | "medium" | "large";

const InvoiceContainer = styled.div<{ disabled: boolean; size: RowSize }>`
  height: 42px;
  display: grid;
  grid-gap: 24px;
  align-items: center;

  opacity: ${(props) => (props.disabled ? 0.4 : null)};
  filter: ${(props) => (props.disabled ? "grayscale(1)" : null)};

  grid-template-areas: ${(props) => {
    switch (props.size) {
      case "large":
        return `"status period amount link"`;
      case "medium":
        return `"status amount link"`;
      case "small":
        return `"status link"`;
    }
  }};

  grid-template-columns: ${(props) => {
    switch (props.size) {
      case "large":
        return "100px auto auto 160px";
      case "medium":
        return "100px auto 160px";
      case "small":
        return "100px auto";
    }
  }};

  & > *:nth-child(1) {
    grid-area: status;
  }

  & > *:nth-child(2) {
    grid-area: period;
    display: ${(props) => (props.size === "large" ? null : "none")};
  }

  & > *:nth-child(3) {
    grid-area: amount;
    display: ${(props) => (props.size === "small" ? "none" : null)};
  }

  & > *:nth-child(4) {
    grid-area: link;
  }
`;

const InvoiceLink = styled(Link)`
  display: flex;
  padding-top: 10px;
  padding-bottom: 10px;
  transform: translateY(0.04em);

  svg {
    font-size: 22px;
    margin-right: 4px;
  }
`;

interface InvoiceProps {
  invoice: StripeInternal.Invoice;
}

const normalizeDate = (date: Date | number) => {
  if (date instanceof Date) {
    return date;
  }

  const normalized = `${date}`.length === 10 ? date * 1000 : date;

  return new Date(normalized);
};

const getReadablePeriod = (start: Date | number, end: Date | number) => {
  const currentLanguage = i18next.language;

  const startDate = normalizeDate(start);
  const endDate = normalizeDate(end);

  const startText = format(startDate, "dd MMM", {
    locale: currentLanguage === "sv-SE" && sv,
  });
  const endText = format(endDate, "dd MMM", {
    locale: currentLanguage === "sv-SE" && sv,
  });

  const startYear = format(startDate, "yyyy", {
    locale: currentLanguage === "sv-SE" && sv,
  });
  const endYear = format(startDate, "yyyy", {
    locale: currentLanguage === "sv-SE" && sv,
  });

  if (isSameDay(startDate, endDate)) {
    return `${startText} ${startYear}`;
  }

  if (isSameYear(startDate, endDate)) {
    return `${startText} - ${endText}, ${endYear}`;
  }

  return `${startText} ${startYear} - ${endText} ${endYear}`;
};

interface InvoiceStatusProps {
  invoice: StripeInternal.Invoice;
}

const InvoiceStatus: FC<InvoiceStatusProps> = (props) => {
  const { t, ready } = useTranslation("account");

  if (!ready) {
    return null;
  }

  if (props.invoice.billing_reason === "upcoming") {
    const upcomingText = t("account.invoices.upcoming");
    return (
      <StatusText
        text={upcomingText}
        severity="info"
        iconLeft={<KvixIcon.Calendar />}
      />
    );
  }

  if (props.invoice.status === "draft") {
    const draftText = t("account.invoices.draft");
    return (
      <StatusText
        text={draftText}
        severity="warning"
        iconLeft={<KvixIcon.Draft />}
      />
    );
  }

  if (props.invoice.paid) {
    const paidText = t("account.invoices.paid");
    return (
      <StatusText
        text={paidText}
        severity="success"
        iconLeft={<KvixIcon.Checkmark.Circled />}
      />
    );
  } else {
    const unpaidText = t("account.invoices.unpaid");
    return (
      <StatusText
        text={unpaidText}
        severity="error"
        iconLeft={<KvixIcon.Error />}
      />
    );
  }
};

const Invoice: FC<InvoiceProps> = (props) => {
  const amount = formatCurrency(
    props.invoice.total / 100,
    props.invoice.currency
  );

  const currentLanguage = i18next.language;
  const { t, ready } = useTranslation("account");
  const nextRecieptDate = formatDistanceToNow(
    normalizeDate(props.invoice.next_payment_attempt),
    { locale: currentLanguage === "sv-SE" && sv }
  );

  const period = useMemo(() => {
    const [firstLine] = props.invoice.lines.data;

    if (firstLine && firstLine.type === "subscription") {
      return getReadablePeriod(firstLine.period.start, firstLine.period.end);
    }

    return "";
  }, [props.invoice.lines.data]);

  const theme = useTheme();
  const isTabletDown = useMediaQuery(theme.breakpoints.down(650));
  const isMobileDown = useMediaQuery(theme.breakpoints.down(450));

  const url = props.invoice.hosted_invoice_url;
  const name = props.invoice.number;

  const isUpcoming = props.invoice.billing_reason === "upcoming";
  const isDraft = props.invoice.status === "draft";

  let size: RowSize = "large";

  if (isTabletDown) {
    size = "medium";
  }

  if (isMobileDown) {
    size = "small";
  }

  if (!ready) {
    return null;
  }

  return (
    <InvoiceContainer disabled={isUpcoming} size={size}>
      {isUpcoming && isMobileDown ? (
        <Text variant="body2" nowrap>
          {t("account.invoices.nextInvoice", { invoiceDate: nextRecieptDate })}
        </Text>
      ) : (
        <InvoiceStatus invoice={props.invoice} />
      )}

      <Text variant="body2">{period}</Text>

      <Box justifySelf="flex-end">
        <Text variant="body2">{amount}</Text>
      </Box>

      <Box justifySelf="flex-end">
        {isUpcoming && !isMobileDown && (
          <Box py={1.5}>
            <Text variant="body2">
              {t("account.invoices.nextReceiptDate", {
                nextReceiptDate: nextRecieptDate,
              })}
            </Text>
          </Box>
        )}

        {!isUpcoming && !isDraft && (
          <InvoiceLink target="_blank" rel="noopener noreferrer" href={url}>
            <KvixIcon.Link />
            {t("account.invoices.downloadReceipt")}
          </InvoiceLink>
        )}
      </Box>
    </InvoiceContainer>
  );
};

export const AccountSectionInvoices: FC = (props) => {
  const { actions, customer } = useContext(PaymentContext);
  const { t, ready } = useTranslation("account");
  const isNativeApp = useIsNativeApp();
  const [invoices, setInvoices] = useState<StripeInternal.Invoice[]>([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState<
    StripeInternal.Invoice[]
  >([]);

  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [anchor, setAnchor] = useState<string>(null);

  const loadUpcomingInvoice = useCallback(async () => {
    if (!customer.subscriptions || customer.subscriptions?.data?.length === 0) {
      return;
    }
    const response = await actions.fetchUpcomingInvoice();

    if (response) {
      setUpcomingInvoice([response]);
    }
  }, [actions]);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);

      const response = await actions
        .fetchInvoices(anchor)
        .finally(() => setLoading(false));
      if (response.data.length > 0) {
        setHasMore(response.has_more);
        setAnchor(response.data[response.data.length - 1].id);
        setInvoices((invoices) => [...invoices, ...response.data]);
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  }, [actions, anchor]);

  useMount(loadUpcomingInvoice);
  useMount(loadInvoices);

  const allInvoices = useMemo(
    () => [...upcomingInvoice, ...invoices],
    [invoices, upcomingInvoice]
  );

  if (allInvoices.length === 0 || !ready) {
    return null;
  }

  const tooltip = t("account.invoices.tooltip");

  return !isNativeApp ? (
    <ProfileSection
      tooltip={tooltip}
      title={<Text variant="h5">{t("account.invoices.title")}</Text>}
    >
      {allInvoices?.length && (
        <>
          {allInvoices.map((invoice, index) => (
            <Fragment key={invoice.id || index}>
              {index !== 0 && <Divider />}

              <Invoice invoice={invoice} />
            </Fragment>
          ))}
        </>
      )}

      {hasMore && (
        <Button
          fullWidth
          variant="text"
          disabled={!hasMore}
          loading={loading}
          onClick={loadInvoices}
        >
          {t("account.invoices.load")}
        </Button>
      )}
    </ProfileSection>
  ) : null;
};
